import React, { useCallback, useState, useEffect } from "react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import ExportDropdownButton from "@/shared/components/ui/Export/ExportDropdownButton";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import { ChannelViewTableColumns } from "@/shared/utils/utils";
import ProspectProfileRow from "@/shared/components/ui/Avatar/ProspectProfileRow";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { getActionLabel, getVariant } from "@/shared/utils/helperFunctions";
import EngagementTabs from "./EngagementRow";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { useGetAnalyticsAggregatedDataForCampaignDashboardByChannel } from "@/features/campaigns/hooks/useCampaign";
import {
  CHANNEL_VIEW_EXPORT_COLUMNS,
  exportCampaignTable,
} from "@/features/campaigns/utils/campaignAnalyticsExport";
import { showError, showSuccess } from "@/shared/utils/toast";
import { X } from "lucide-react";

const ROWS_PER_PAGE = 10;

const formatStepAction = (stepAction) =>
  stepAction ? getActionLabel(stepAction) : "N/A";

const ChannelViewModal = ({ isOpen, onClose, action, channel, from, to }) => {
  const [page, setPage] = useState(1);
  const campaignId = useCampaignId();
  const {
    data: analyticsAggregatedDataByChannel,
    isLoading: isLoadingAnalyticsAggregatedDataByChannel,
  } = useGetAnalyticsAggregatedDataForCampaignDashboardByChannel(
    campaignId,
    action?.toLowerCase(),
    channel?.toLowerCase(),
    from,
    to,
  );

  const totalCount = analyticsAggregatedDataByChannel?.length ?? 0;

  const totalPages =
    ROWS_PER_PAGE > 0 ? Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE)) : 1;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, campaignId, action, channel, from, to]);

  const renderCell = useCallback((item, columnKey) => {
    switch (columnKey) {
      case "full_name":
        return (
          <ProspectProfileRow
            avatarUrl={item?.avatarUrl}
            name={item?.full_name}
            email={item?.email}
            number={item?.matchScore}
            className="text-wrap break-words"
          />
        );

      case "company":
        return (
          <div className="text-xs text-gray-500 text-wrap text-break">
            {item.company}
          </div>
        );

      case "step_order":
        return (
          <StatusChip
            label={`Step ${item?.step_order}`}
            variant={getVariant("neutral")}
          />
        );

      case "step_action":
        return (
          <StatusChip
            label={formatStepAction(item?.step_action)}
            variant={getVariant("neutral")}
          />
        );

      case "engagement":
        return (
          <div className="flex flex-col gap-1 text-xs items-center justify-center text-wrap break-words">
            <EngagementTabs item={item} action={action} />
          </div>
        );

      default:
        return (
          <p className="text-xs text-gray-500 text-wrap break-words">
            {item[columnKey]?.charAt(0)?.toUpperCase() +
              item[columnKey]?.slice(1)}
          </p>
        );
    }
  }, [action]);

  const modalTitle = `${action || "Engaged"} Contacts${channel ? ` (${channel})` : ""}`;

  const handleExportContacts = async (format) => {
    try {
      const rows = analyticsAggregatedDataByChannel ?? [];
      if (!rows.length) {
        showError("No contacts available to export.");
        return;
      }

      await exportCampaignTable({
        format,
        columns: CHANNEL_VIEW_EXPORT_COLUMNS,
        rows,
        filename: `${action || "engaged"}-${channel || "all"}-contacts`
          .toLowerCase()
          .replace(/\s+/g, "-"),
        title: modalTitle,
      });
      showSuccess(`${modalTitle} exported successfully.`);
    } catch (error) {
      showError(error.message || "Failed to export engaged contacts.");
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      width="1100px"
      className="!items-stretch !p-6"
    >
      {isLoadingAnalyticsAggregatedDataByChannel ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <Loader text="Loading engaged contacts..." />
        </div>
      ) : (
        <div className="flex h-[min(560px,calc(90dvh-3rem))] min-h-0 w-full flex-col gap-4 overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-900">{modalTitle}</h2>
            <div className="flex items-center gap-2">
              <ExportDropdownButton
                onExport={handleExportContacts}
                disabled={!analyticsAggregatedDataByChannel?.length}
              />
              <button
                type="button"
                className="rounded-full p-1 hover:bg-gray-100"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={20} color="#181D27" />
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <CustomTable
              data={analyticsAggregatedDataByChannel ?? []}
              columns={ChannelViewTableColumns}
              renderCell={renderCell}
              totalCount={totalCount}
              rowsPerPage={ROWS_PER_PAGE}
              page={page}
              onPageChange={setPage}
              isServerSide={false}
              height="100%"
            />
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default ChannelViewModal;
